import {
  inject,
  Injectable,
  Signal,
} from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { HttpTransportType } from '@microsoft/signalr';
import { Observable, Subscriber } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { INotification } from '../entities/client-notifications.interface';
import { APP_CONFIG } from '@application/config';
import { AuthStore, IAuthAccessToken } from '@auth/data-access';
@Injectable({
  providedIn: 'root'
})
export class ClientNotificationService {
  private readonly authStore = inject(AuthStore);
  private readonly authData: Signal<IAuthAccessToken> = this.authStore.authData;
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(APP_CONFIG).apiUrl;

  public openConnection(eventNames: string[], url?: string): Observable<any> {
    return new Observable((obs) => {
      const connection = new signalR.HubConnectionBuilder()
        .withUrl(
          this.apiUrl?.[url ?? 'client_notification'] +
          `/NotificationHub/?TenantId=${this.authData().TenantId}&UserId=${this.authData().PersonId}`,
          { skipNegotiation: true, transport: HttpTransportType.WebSockets }
        )
        .withAutomaticReconnect([5000, 5000, 5000, 10000])
        .build();

      const subsc = new Observable(
        (
          observer: Subscriber<{
            eventName: string;
            eventData: string | object;
          }>
        ) => {
          for (const eventName of eventNames) {
            connection.on(eventName, (data) => {
              const eventData: any = {};

              const defaultProccess = [
                'onCentralNotification',
                'onCentralNotificationContract',
                'onUpdatePersonDatasNotification',
                'onAccessNotification',
                'onGympassCheckinNotification',
                'onTotalpassCheckinNotification',
                'onUserPhotoUploadedNotification'
              ];
              const personProccess = [
                'onGympassCheckinNotification',
                'onTotalpassCheckinNotification'
              ];

              const consumptionProccess = [
                'onCompletedConsumptionNotification'
              ];

              if (defaultProccess.includes(eventName)) {
                for (const [key, value] of Object.entries(data)) {
                  eventData[this.turnPascalCase(key)] = value;
                }
              }

              if (personProccess.includes(eventName)) {
                eventData['Person'] = {};
                for (const [key, value] of Object.entries(data.person)) {
                  eventData['Person'][this.turnPascalCase(key)] = value;
                }
              }

              if (consumptionProccess.includes(eventName)) {
                eventData['Consumption'] = {};
                for (const [key, value] of Object.entries(data.consumption || data || {})) {
                  eventData['Consumption'][this.turnPascalCase(key)] = value;
                }
              }

              if (eventName === 'onAccessRealTimeOccupancyNotification') {
                eventData['CurrentOccupancy'] = data;
              }

              observer.next({
                eventName,
                eventData,
              });
            });
          }

          connection.start().then(() => {
            observer.next({
              eventName: 'onConnected',
              eventData: 'ok',
            });
          }).catch((error) => {
            observer.error(error);
          });

          connection.onclose((e) => {
            observer.next({
              eventName: 'onConnectionClose',
              eventData: e,
            });
          });

          connection.onreconnecting((e) => {
            observer.next({
              eventName: 'onConnectionReconnecting',
              eventData: e,
            });
          });

          connection.onreconnected((e) => {
            observer.next({
              eventName: 'onConnectionReconnected',
              eventData: e,
            });
          });
        }
      );

      subsc.subscribe({
        next: (res) => {
          obs.next(res);
        },
        error: (error) => {
          obs.next(error);
          obs.complete();
        },
      });

      return function unsubscribe() {
        connection.stop();
      };
    });
  }

  private turnPascalCase(key: string): string {
    return `${key[0].toUpperCase()}${key.substring(1, key.length)}`;
  }

  public getNotifications(
    page: number,
    take: number,
    expandContent: boolean
  ): Observable<INotification[]> {
    return this.http.get<INotification[]>(
      this.apiUrl.client_notification +
      `/Notifications/GetNotifications?tenantId=${this.authData().TenantId}&userId=${this.authData().PersonId}&page=${page}&take=${take}&expandContent=${expandContent}`,
      {
        withCredentials: true,
      }
    );
  }

  public setReadedAllNotifications(): Observable<null> {
    return this.http.post<null>(
      this.apiUrl.client_notification +
      `/Notifications/SetReaded?tenantId=${this.authData().TenantId}&userId=${this.authData().PersonId}`,
      {},
      {
        withCredentials: true,
      }
    );
  }

  public deleteNotification(notificationId: string): Observable<null> {
    return this.http.delete<null>(
      this.apiUrl.client_notification +
      `/Notifications/Delete`,
      {
        params: {
          notificationId
        },
        withCredentials: true,
      }
    );
  }
}
